/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

import type { Optional } from '../../@types/common';
import type { IThreadBundledRelationship } from '../../@types/event';
import type { EventTimelineSetHandlerMap } from '../../@types/event-timeline-set';
import type { CachedReceiptStructure, Receipt } from '../../@types/read_receipts';
import type { NotificationCountType } from '../../@types/room';
import type { XHubChatClient } from '../../client';
import type { Room } from '../room/room';
import type { RoomState } from '../room/room-state';

import { PendingEventOrdering } from '../../@types/client';
import { RelationType, XHubChatEventEvent } from '../../@types/event';
import { ReceiptType } from '../../@types/read_receipts';
import { RoomEvent } from '../../@types/room';
import { TypedReEmitter } from '../../emit/ReEmitter';
import { Feature, ServerSupport } from '../../utils/feature';
import { logger } from '../../utils/logger';
import { ServerControlledNamespacedValue } from '../../utils/NamespacedValue';
import { XHubChatEvent } from '../event/event';
import { Direction, EventTimeline } from '../event/event-timeline';
import { EventTimelineSet } from '../event/event-timeline-set';
import { ReadReceipt } from '../read-receipt';

export enum ThreadEvent {
  New = 'Thread.new',
  Update = 'Thread.update',
  NewReply = 'Thread.newReply',
  ViewThread = 'Thread.viewThread',
  Delete = 'Thread.delete',
}

export type ThreadEmittedEvents = Exclude<ThreadEvent, ThreadEvent.New> | RoomEvent.Timeline | RoomEvent.TimelineReset;

export type ThreadEventHandlerMap = {
  [ThreadEvent.Update]: (thread: Thread) => void;
  [ThreadEvent.NewReply]: (thread: Thread, event: XHubChatEvent) => void;
  [ThreadEvent.ViewThread]: () => void;
  [ThreadEvent.Delete]: (thread: Thread) => void;
} & EventTimelineSetHandlerMap;

type IThreadOpts = {
  room: Room;
  client: XHubChatClient;
  pendingEventOrdering?: PendingEventOrdering;
  receipts?: CachedReceiptStructure[];
};

export enum FeatureSupport {
  None = 0,
  Experimental = 1,
  Stable = 2,
}

export function determineFeatureSupport(stable: boolean, unstable: boolean): FeatureSupport {
  if (stable) {
    return FeatureSupport.Stable;
  } else if (unstable) {
    return FeatureSupport.Experimental;
  } else {
    return FeatureSupport.None;
  }
}

export const FILTER_RELATED_BY_SENDERS = new ServerControlledNamespacedValue(
  'related_by_senders',
  'io.element.relation_senders',
);
export const FILTER_RELATED_BY_REL_TYPES = new ServerControlledNamespacedValue(
  'related_by_rel_types',
  'io.element.relation_types',
);
export const THREAD_RELATION_TYPE = new ServerControlledNamespacedValue('m.thread', 'io.element.thread');

export class Thread extends ReadReceipt<ThreadEmittedEvents, ThreadEventHandlerMap> {
  public static hasServerSideSupport = FeatureSupport.Stable;
  public static hasServerSideListSupport = FeatureSupport.None;
  public static hasServerSideFwdPaginationSupport = FeatureSupport.None;

  /**
   * A reference to all the events ID at the bottom of the threads
   */
  public readonly timelineSet: EventTimelineSet;

  private _currentUserParticipated = false;

  private reEmitter: TypedReEmitter<ThreadEmittedEvents, ThreadEventHandlerMap>;

  /**
   * The last event in this thread, if we don't yet have this in the timeline.
   *
   * When we run {@link processRootEvent} (which I think happens during the
   * setting-up of the thread), we set this to the event pointed to by the
   * server in `latest_event` [1] that came through with the thread root.
   *
   * [1]: https://spec.matrix.org/v1.8/client-server-api/#server-side-aggregation-of-mthread-relationships
   *
   * Later, when we have populated the timeline, this is set to undefined, so
   * that methods like {@link replyToEvent} fall through to use lastReply,
   * which looks in the timeline for the latest event that is a "thread reply"
   * i.e. directly refers to the thread root with an m.thread relation.
   *
   * So it looks like this is only really relevant when initialEventsFetched
   * is false, because as soon as the initial events have been fetched, we
   * should have a proper chunk of timeline from the pagination fetch.
   *
   * If all replies in this thread are redacted, this is set to the root
   * event. I'm not clear what the meaning of this is, since usually after the
   * initial events have been fetched, lastEvent should be undefined.
   * In fact, the whole usage inside onRedaction looks suspect - it may be
   * that we were thinking lastEvent always refers to the actual last event,
   * but it only does so before initialEventsFetched becomes true.
   *
   * The usage of lastEvent inside {@link onEcho} looks suspicious, since I'd
   * think we probably mean {@link replyToEvent} there - we are trying not to
   * echo a duplicate event, and we probably want that behaviour even after
   * initialEventsFetched has become true.
   *
   * -- andyb
   */
  private lastEvent: XHubChatEvent | undefined;

  private replyCount = 0;
  private lastPendingEvent: XHubChatEvent | undefined;
  private pendingReplyCount = 0;

  public readonly room: Room;
  public readonly client: XHubChatClient;
  private readonly pendingEventOrdering: PendingEventOrdering;
  private processRootEventPromise?: Promise<void>;

  /**
   * Whether or not we need to fetch the initial set of events for the thread. We can
   * only do this if the server has support for it, so if it doesn't we just pretend
   * that we've already fetched them.
   */
  public initialEventsFetched = !Thread.hasServerSideSupport;
  private initialEventFetchPromise: Promise<boolean> | undefined;

  /**
   * An array of events to add to the timeline once the thread has been initialized
   * with server support.
   */
  public replayEvents: XHubChatEvent[] | null = [];

  public constructor(public readonly id: string, public rootEvent: XHubChatEvent | undefined, opts: IThreadOpts) {
    super();

    // each Event in the thread adds a re-emitter, so we could hit the listener limit.
    this.setMaxListeners(1000);

    if (!opts?.room) {
      // Logging/debugging for https://github.com/vector-im/element-web/issues/22141
      // Hope is that we end up with a more obvious stack trace.
      throw new Error('element-web#22141: A thread requires a room in order to function');
    }

    this.room = opts.room;
    this.client = opts.client;
    this.pendingEventOrdering = opts.pendingEventOrdering ?? PendingEventOrdering.Chronological;
    this.timelineSet = new EventTimelineSet(
      this.room,
      {
        timelineSupport: true,
        pendingEvents: true,
      },
      this.client,
      this,
    );
    this.reEmitter = new TypedReEmitter(this);

    this.reEmitter.reEmit(this.timelineSet, [RoomEvent.Timeline, RoomEvent.TimelineReset]);

    this.room.on(XHubChatEventEvent.BeforeRedaction, this.onBeforeRedaction);
    this.room.on(RoomEvent.Redaction, this.onRedaction);
    this.room.on(RoomEvent.LocalEchoUpdated, this.onLocalEcho);
    this.room.on(RoomEvent.TimelineReset, this.onTimelineReset);
    this.timelineSet.on(RoomEvent.Timeline, this.onTimelineEvent);

    this.processReceipts(opts.receipts);

    // even if this thread is thought to be originating from this client, we initialize it as we may be in a
    // gappy sync and a thread around this event may already exist.
    this.updateThreadMetadata();
    this.setEventMetadata(this.rootEvent);
  }

  private onTimelineReset = async (): Promise<void> => {
    // We hit a gappy sync, ask the server for an update
    await this.processRootEventPromise;
    this.processRootEventPromise = undefined;
  };

  private onBeforeRedaction = (event: XHubChatEvent, redaction: XHubChatEvent): void => {
    if (
      event?.isRelation(THREAD_RELATION_TYPE.name)
      && this.room.eventShouldLiveIn(event).threadId === this.id
      && event.getId() !== this.id // the root event isn't counted in the length so ignore this redaction
      && !redaction.status // only respect it when it succeeds
    ) {
      this.replyCount--;
      this.updatePendingReplyCount();
      this.emit(ThreadEvent.Update, this);
    }
  };

  private onRedaction = async (event: XHubChatEvent, room: Room, threadRootId?: string): Promise<void> => {
    if (threadRootId !== this.id) return; // ignore redaction for other timelines
    if (this.replyCount <= 0) {
      for (const threadEvent of this.timeline) {
        this.clearEventMetadata(threadEvent);
      }
      this.lastEvent = this.rootEvent;
      this._currentUserParticipated = false;
      this.emit(ThreadEvent.Delete, this);
    } else {
      if (this.lastEvent?.getId() === event.getAssociatedId()) {
        // XXX: If our last event got redacted we query the server for the last event once again
        await this.processRootEventPromise;
        this.processRootEventPromise = undefined;
      }
      await this.updateThreadMetadata();
    }
  };

  private async fetchRootEvent(): Promise<void> {
    // Always fetch the root event, even if we already have it, so we can get the latest
    // state (via unsigned).
    try {
      const eventData = await this.client.fetchRoomEvent(this.room.roomData.category, this.id);
      const mapper = this.client.getEventMapper();
      this.rootEvent = mapper(eventData); // will merge with existing event object if such is known
    } catch (e) {
      logger.error('Failed to fetch thread root to construct thread with', e);
    }
    await this.processEvent(this.rootEvent);
  }

  public static setServerSideSupport(status: FeatureSupport): void {
    Thread.hasServerSideSupport = status;
    // XXX: This global latching behaviour is really unexpected and means that you can't undo when moving to a server without support
    if (status !== FeatureSupport.Stable) {
      FILTER_RELATED_BY_SENDERS.setPreferUnstable(true);
      FILTER_RELATED_BY_REL_TYPES.setPreferUnstable(true);
      THREAD_RELATION_TYPE.setPreferUnstable(true);
    }
  }

  public static setServerSideListSupport(status: FeatureSupport): void {
    Thread.hasServerSideListSupport = status;
  }

  public static setServerSideFwdPaginationSupport(status: FeatureSupport): void {
    Thread.hasServerSideFwdPaginationSupport = status;
  }

  private onTimelineEvent = (
    event: XHubChatEvent,
    room: Room | undefined,
    toStartOfTimeline: boolean | undefined,
  ): void => {
    // Add a synthesized receipt when paginating forward in the timeline
    if (!toStartOfTimeline) {
      const sender = event.getSender();
      if (sender && room && this.shouldSendLocalEchoReceipt(sender, event)) {
        room.addLocalEchoReceipt(sender, event, ReceiptType.Read);
      }
      if (event.getId() !== this.id && event.isRelation(THREAD_RELATION_TYPE.name)) {
        this.replyCount++;
      }
    }
    this.onEcho(event, toStartOfTimeline ?? false);
  };

  private shouldSendLocalEchoReceipt(sender: string, event: XHubChatEvent): boolean {
    const recursionSupport = this.client.canSupport.get(Feature.RelationsRecursion) ?? ServerSupport.Unsupported;

    if (recursionSupport === ServerSupport.Unsupported) {
      // Normally we add a local receipt, but if we don't have
      // recursion support, then events may arrive out of order, so we
      // only create a receipt if it's after our existing receipt.
      const oldReceiptEventId = this.getReadReceiptForUserId(sender)?.eventId;
      if (oldReceiptEventId) {
        const receiptEvent = this.findEventById(oldReceiptEventId);
        if (receiptEvent && receiptEvent.getTs() > event.getTs()) {
          return false;
        }
      }
    }

    return true;
  }

  private onLocalEcho = (event: XHubChatEvent): void => {
    this.onEcho(event, false);
  };

  private onEcho = async (event: XHubChatEvent, toStartOfTimeline: boolean): Promise<void> => {
    if (event.threadRootId !== this.id) return; // ignore echoes for other timelines
    if (this.lastEvent === event) return; // ignore duplicate events
    await this.updateThreadMetadata();
    if (!event.isRelation(THREAD_RELATION_TYPE.name)) return; // don't send a new reply event for reactions or edits
    if (toStartOfTimeline) return; // ignore messages added to the start of the timeline
    // Clear the lastEvent and instead start tracking locally using lastReply
    this.lastEvent = undefined;
    this.emit(ThreadEvent.NewReply, this, event);
  };

  public get roomState(): RoomState {
    return this.room.getLiveTimeline().getState(EventTimeline.FORWARDS)!;
  }

  private addEventToTimeline(event: XHubChatEvent, toStartOfTimeline: boolean): void {
    if (!this.findEventById(event.getId()!)) {
      this.timelineSet.addEventToTimeline(event, this.liveTimeline, {
        toStartOfTimeline,
        fromCache: false,
        roomState: this.roomState,
        addToState: false,
      });
    }
  }

  /**
   * TEMPORARY. Only call this when MSC3981 is not available, and we have some
   * late-arriving events to insert, because we recursively found them as part
   * of populating a thread. When we have MSC3981 we won't need it, because
   * they will all be supplied by the BE server in one request, and they will
   * already be in the right order in that response.
   * This is a copy of addEventToTimeline above, modified to call
   * insertEventIntoTimeline so this event is inserted into our best guess of
   * the right place based on timestamp. (We should be using Sync Order but we
   * don't have it.)
   *
   * @internal
   */
  public insertEventIntoTimeline(event: XHubChatEvent): void {
    const eventId = event.getId();
    if (!eventId) {
      return;
    }
    // If the event is already in this thread, bail out
    if (this.findEventById(eventId)) {
      return;
    }
    this.timelineSet.insertEventIntoTimeline(event, this.liveTimeline, this.roomState, false);
  }

  public addEvents(events: XHubChatEvent[], toStartOfTimeline: boolean): void {
    events.forEach(ev => this.addEvent(ev, toStartOfTimeline, false));
    this.updateThreadMetadata();
  }

  /**
   * Add an event to the thread and updates
   * the tail/root references if needed
   * Will fire "Thread.update"
   * @param event - The event to add
   * @param toStartOfTimeline - whether the event is being added
   * to the start (and not the end) of the timeline.
   * @param emit - whether to emit the Update event if the thread was updated or not.
   */
  public addEvent(event: XHubChatEvent, toStartOfTimeline: boolean, emit = true): void {
    // Modify this event to point at our room's state, and mark its thread
    // as this.
    this.setEventMetadata(event);

    // Decide whether this event is going to be added at the end of the timeline.
    const lastReply = this.lastReply();
    const isNewestReply = !lastReply || event.localTimestamp >= lastReply!.localTimestamp;

    if (!Thread.hasServerSideSupport) {
      // When there's no server-side support, just add it to the end of the timeline.
      this.addEventToTimeline(event, toStartOfTimeline);
      this.client.decryptEventIfNeeded(event);
    } else if (event.isRelation(RelationType.Annotation)) {
      this.addRelatedThreadEvent(event, toStartOfTimeline);
      return;
    } else if (!toStartOfTimeline && isNewestReply) {
      // When we've asked for the event to be added to the end,
      // and this event belongs at the end, add it.
      this.addEventToTimeline(event, false);
    } else {
      // Otherwise, we should add it, but we suspect it is out of order.
      // This may be because we've just created the thread object and are
      // still fetching events, in which case add it where we think is sensible
      // and it will be removed and replaced with the events from the pagination
      // request once that completes.
      if (toStartOfTimeline) {
        // If we're adding at the start of the timeline, it doesn't
        // matter that it's out of order.
        this.addEventToTimeline(event, toStartOfTimeline);
      } else {
        // We think this event might be out of order, because isNewestReply
        // is false (otherwise we would have gone into the earlier if
        // clause), so try to insert it in the right place based on
        // timestamp.
        this.insertEventIntoTimeline(event);
      }
    }

    if (
      event.getId() !== this.id
      && event.isRelation(THREAD_RELATION_TYPE.name)
      && !toStartOfTimeline
      && isNewestReply
    ) {
      // Clear the last event as we have the latest end of the timeline
      this.lastEvent = undefined;
    }

    if (emit) {
      this.emit(ThreadEvent.NewReply, this, event);
      this.updateThreadMetadata();
    }
  }

  private addRelatedThreadEvent(event: XHubChatEvent, toStartOfTimeline: boolean): void {
    // If this event is not a direct member of the thread, but is a
    // reference to something that is, then we have two cases:

    if (!this.initialEventsFetched) {
      // Case 1: we haven't yet fetched events from the server. In
      // this case, when we do, the events we get back might only be
      // the first-order ones, so this event (which is second-order -
      // a reference to something directly in the thread) needs to be
      // kept so we can replay it when the first-order ones turn up.

      /**
       * A thread can be fully discovered via a single sync response
       * And when that's the case we still ask the server to do an initialization
       * as it's the safest to ensure we have everything.
       * However when we are in that scenario we might lose annotation or edits
       *
       * This fix keeps a reference to those events and replay them once the thread
       * has been initialized properly.
       */
      this.replayEvents?.push(event);

      // For annotations (reactions), aggregate immediately (pre-init) to keep
      // reaction counts/summary visible while the thread is still initializing.
      // Only aggregate as child: parent aggregation is unnecessary here.
      if (event.isRelation(RelationType.Annotation)) {
        this.timelineSet.relations?.aggregateChildEvent(event, this.timelineSet);
      }
    } else {
      // Case 2: this is happening later, and we have a timeline. In
      // this case, these events might be out-of order.
      //
      // Specifically, if the server doesn't support recursion, so we
      // only get these events through sync, they might be coming
      // later than the first-order ones, so we insert them based on
      // timestamp (despite the problems with this documented in
      // #3325).
      //
      // If the server does support recursion, we should have got all
      // the interspersed events from the server when we fetched the
      // initial events, so if they are coming via sync they should be
      // the latest ones, so we can add them as normal.
      //
      // (Note that both insertEventIntoTimeline and addEventToTimeline
      // do nothing if we have seen this event before.)

      const recursionSupport
        = this.client.canSupport.get(Feature.RelationsRecursion) ?? ServerSupport.Unsupported;

      if (recursionSupport === ServerSupport.Unsupported) {
        this.insertEventIntoTimeline(event);
      } else {
        this.addEventToTimeline(event, toStartOfTimeline);
      }
      // Aggregation is handled by EventTimelineSet when inserting/adding.
    }
  }

  public async processEvent(event: Optional<XHubChatEvent>): Promise<void> {
    if (event) {
      this.setEventMetadata(event);
    }
  }

  /**
   * Processes the receipts that were caught during initial sync
   * When clients become aware of a thread, they try to retrieve those read receipts
   * and apply them to the current thread
   * @param receipts - A collection of the receipts cached from initial sync
   */
  private processReceipts(receipts: CachedReceiptStructure[] = []): void {
    for (const { eventId, receiptType, userId, receipt, synthetic } of receipts) {
      this.addReceiptToStructure(eventId, receiptType as ReceiptType, userId, receipt, synthetic);
    }
  }

  private getRootEventBundledRelationship(rootEvent = this.rootEvent): IThreadBundledRelationship | undefined {
    return rootEvent?.getServerAggregatedRelation<IThreadBundledRelationship>(THREAD_RELATION_TYPE.name);
  }

  private async processRootEvent(): Promise<void> {
    const bundledRelationship = this.getRootEventBundledRelationship();
    if (Thread.hasServerSideSupport && bundledRelationship) {
      this.replyCount = bundledRelationship.count;
      this._currentUserParticipated = !!bundledRelationship.current_user_participated;

      const mapper = this.client.getEventMapper();
      // re-insert roomId
      this.lastEvent = mapper({
        ...bundledRelationship.latest_event,
        room_id: this.roomId,
      });
      this.updatePendingReplyCount();
      await this.processEvent(this.lastEvent);
    }
  }

  private updatePendingReplyCount(): void {
    const unfilteredPendingEvents
      = this.pendingEventOrdering === PendingEventOrdering.Detached ? this.room.getPendingEvents() : this.events;
    const pendingEvents = unfilteredPendingEvents.filter(
      ev =>
        ev.threadRootId === this.id
        && ev.isRelation(THREAD_RELATION_TYPE.name)
        && ev.status !== null
        && ev.getId() !== this.lastEvent?.getId(),
    );
    this.lastPendingEvent = pendingEvents.length ? pendingEvents[pendingEvents.length - 1] : undefined;
    this.pendingReplyCount = pendingEvents.length;
  }

  private async updateThreadFromRootEvent(): Promise<void> {
    if (Thread.hasServerSideSupport) {
      // Ensure we show *something* as soon as possible, we'll update it as soon as we get better data, but we
      // don't want the thread preview to be empty if we can avoid it
      if (!this.initialEventsFetched && !this.lastEvent) {
        await this.processRootEvent();
      }
      await this.fetchRootEvent();
    }
    await this.processRootEvent();
  }

  private async updateThreadMetadata(): Promise<void> {
    this.updatePendingReplyCount();

    if (!this.processRootEventPromise) {
      // We only want to do this once otherwise we end up rolling back to the last unsigned summary we have for the thread
      this.processRootEventPromise = this.updateThreadFromRootEvent();
    }
    await this.processRootEventPromise;

    if (!this.initialEventsFetched) {
      if (this.initialEventFetchPromise) {
        await this.initialEventFetchPromise;
      } else {
        // fetch initial events to allow proper pagination
        try {
          // clear out any events that were added before the pagination request
          // completed (eg. from sync). They'll be replaced by those from the pagination.
          // Really, we should do this after the pagination request completes, but
          // paginateEventTimeline does the request and adds the events in one go, so
          // this would need a refactor in order to do. It's therefore possible there's
          // a remaining race where an event comes in while the pagination request is
          // happening.
          this.timelineSet.resetLiveTimeline();
          // if the thread has regular events, this will just load the last reply.
          // if the thread is newly created, this will load the root event.
          if (this.replyCount === 0 && this.rootEvent) {
            this.timelineSet.addEventsToTimeline([this.rootEvent], true, false, this.liveTimeline, null);
            this.liveTimeline.setPaginationToken(null, Direction.Backward);
          } else {
            this.initialEventFetchPromise = this.client.paginateEventTimeline(this.liveTimeline, {
              backwards: true,
            });
            await this.initialEventFetchPromise;
          }
          // We have now fetched the initial events, so set the flag. We need to do this before
          // we actually add the events, so `this.addEvents` knows that it can now safely add
          // them rather than buffer them in the pending event list. The main thing is that this
          // must remain false while the async fetch happens, so we don't try to add events before
          // the pagination has finished. The important thing is that we're not await-ing anything
          // else between setting this and adding events, so no races.
          this.initialEventsFetched = true;
          for (const event of this.replayEvents!) {
            this.addEvent(event, false);
          }
          this.replayEvents = null;
          // just to make sure that, if we've created a timeline window for this thread before the thread itself
          // existed (e.g. when creating a new thread), we'll make sure the panel is force refreshed correctly.
          this.emit(RoomEvent.TimelineReset, this.room, this.timelineSet, true);
        } catch (e) {
          logger.error('Failed to load start of newly created thread: ', e);
          this.initialEventsFetched = false;
        }
      }
    }

    this.emit(ThreadEvent.Update, this);
  }

  public setEventMetadata(event: Optional<XHubChatEvent>): void {
    if (event) {
      EventTimeline.setEventMetadata(event, this.roomState, false);
      event.setThread(this);
    }
  }

  public clearEventMetadata(event: Optional<XHubChatEvent>): void {
    if (event) {
      event.setThread(undefined);
      delete event.event?.unsigned?.['m.relations']?.[THREAD_RELATION_TYPE.name];
    }
  }

  /**
   * Finds an event by ID in the current thread
   */
  public findEventById(eventId: string): XHubChatEvent | undefined {
    return this.timelineSet.findEventById(eventId);
  }

  /**
   * Return last reply to the thread, if known.
   */
  public lastReply(
    matches: (ev: XHubChatEvent) => boolean = (ev): boolean => ev.isRelation(THREAD_RELATION_TYPE.name),
  ): XHubChatEvent | null {
    for (let i = this.timeline.length - 1; i >= 0; i--) {
      const event = this.timeline[i];
      if (matches(event)) {
        return event;
      }
    }
    return null;
  }

  public get roomId(): string {
    return this.room.roomId;
  }

  /**
   * The number of messages in the thread
   * Only count rel_type=m.thread as we want to
   * exclude annotations from that number
   */
  public get length(): number {
    return this.replyCount + this.pendingReplyCount;
  }

  /**
   * A getter for the last event of the thread.
   * This might be a synthesized event, if so, it will not emit any events to listeners.
   */
  public get replyToEvent(): Optional<XHubChatEvent> {
    return this.lastPendingEvent ?? this.lastEvent ?? this.lastReply();
  }

  /**
   * The live event timeline for this thread.
   * @deprecated Present for backwards compatibility.
   *             Use this.events instead
   * @returns The live event timeline for this thread.
   */
  public get timeline(): XHubChatEvent[] {
    return this.events;
  }

  public get events(): XHubChatEvent[] {
    return this.liveTimeline.getEvents();
  }

  public has(eventId: string): boolean {
    return this.timelineSet.findEventById(eventId) instanceof XHubChatEvent;
  }

  public get hasCurrentUserParticipated(): boolean {
    return this._currentUserParticipated;
  }

  public get liveTimeline(): EventTimeline {
    return this.timelineSet.getLiveTimeline();
  }

  public getUnfilteredTimelineSet(): EventTimelineSet {
    return this.timelineSet;
  }

  public addReceipt(event: XHubChatEvent, synthetic: boolean): void {
    throw new Error('Unsupported function on the thread model');
  }

  /**
   * Get the ID of the event that a given user has read up to within this thread,
   * or null if we have received no read receipt (at all) from them.
   * @param userId - The user ID to get read receipt event ID for
   * @param ignoreSynthesized - If true, return only receipts that have been
   *                            sent by the server, not implicit ones generated
   *                            by the JS SDK.
   * @returns ID of the latest event that the given user has read, or null.
   */
  public getEventReadUpTo(userId: string, ignoreSynthesized?: boolean): string | null {
    // TODO: we think the implementation here is not right. Here is a sketch
    // of the right answer:
    //
    // for event in timeline.events.reversed():
    //     if room.hasUserReadEvent(event):
    //         return event
    // return null
    //
    // If this is too slow, we might be able to improve it by trying walking
    // forward from the threaded receipt in this thread. We could alternate
    // between backwards-from-front and forwards-from-threaded-receipt to
    // improve our chances of hitting the right answer sooner.
    //
    // Either way, it's still fundamentally slow because we have to walk
    // events.
    //
    // We also might just want to limit the time we spend on this by giving
    // up after, say, 100 events.
    //
    // --- andyb

    const isCurrentUser = userId === this.client.getUserId();
    const lastReply = this.timeline[this.timeline.length - 1];
    if (isCurrentUser && lastReply) {
      // If the last activity in a thread is prior to the first threaded read receipt
      // sent in the room (suggesting that it was sent before the user started
      // using a client that supported threaded read receipts), we want to
      // consider this thread as read.
      const beforeFirstThreadedReceipt = lastReply.getTs() < this.room.getOldestThreadedReceiptTs();
      const lastReplyId = lastReply.getId();
      // Some unsent events do not have an ID, we do not want to consider them read
      if (beforeFirstThreadedReceipt && lastReplyId) {
        return lastReplyId;
      }
    }

    const readUpToId = super.getEventReadUpTo(userId, ignoreSynthesized);

    // Check whether the unthreaded read receipt for that user is more recent
    // than the read receipt inside that thread.
    if (lastReply) {
      const unthreadedReceipt = this.room.getLastUnthreadedReceiptFor(userId);
      if (!unthreadedReceipt) {
        return readUpToId;
      }

      for (let i = this.timeline?.length - 1; i >= 0; --i) {
        const ev = this.timeline[i];
        // If we encounter the `readUpToId` we do not need to look further
        // there is no "more recent" unthreaded read receipt
        if (ev.getId() === readUpToId) return readUpToId;

        // Inspecting events from most recent to oldest, we're checking
        // whether an unthreaded read receipt is more recent that the current event.
        // We usually prefer relying on the order of the DAG but in this scenario
        // it is not possible and we have to rely on timestamp
        if (ev.getTs() < unthreadedReceipt.ts) return ev.getId() ?? readUpToId;
      }
    }

    return readUpToId;
  }

  /**
   * Determine if the given user has read a particular event.
   *
   * It is invalid to call this method with an event that is not part of this thread.
   *
   * This is not a definitive check as it only checks the events that have been
   * loaded client-side at the time of execution.
   * @param userId - The user ID to check the read state of.
   * @param eventId - The event ID to check if the user read.
   * @returns True if the user has read the event, false otherwise.
   */
  public hasUserReadEvent(userId: string, eventId: string): boolean {
    if (userId === this.client.getUserId()) {
      // Consider an event read if it's part of a thread that is before the
      // first threaded receipt sent in that room. It is likely that it is
      // part of a thread that was created before MSC3771 was implemented.
      // Or before the last unthreaded receipt for the logged in user
      const beforeFirstThreadedReceipt
        = (this.lastReply()?.getTs() ?? 0) < this.room.getOldestThreadedReceiptTs();
      const unthreadedReceiptTs = this.room.getLastUnthreadedReceiptFor(userId)?.ts ?? 0;
      const beforeLastUnthreadedReceipt = (this?.lastReply()?.getTs() ?? 0) < unthreadedReceiptTs;
      if (beforeFirstThreadedReceipt || beforeLastUnthreadedReceipt) {
        return true;
      }
    }

    return this.room.hasUserReadEvent(userId, eventId);
  }

  public setUnread(type: NotificationCountType, count: number): void {
    return this.room.setThreadUnreadNotificationCount(this.id, type, count);
  }

  /**
   * Returns the most recent unthreaded receipt for a given user
   * @param userId - the MxID of the User
   * @returns an unthreaded Receipt. Can be undefined if receipts have been disabled
   * or a user chooses to use private read receipts (or we have simply not received
   * a receipt from this user yet).
   */
  public getLastUnthreadedReceiptFor(userId: string): Receipt | undefined {
    return this.room.getLastUnthreadedReceiptFor(userId);
  }
}

export enum ThreadFilterType {
  My,
  All,
}

export function threadFilterTypeToFilter(type: ThreadFilterType | null): 'all' | 'participated' {
  switch (type) {
    case ThreadFilterType.My:
      return 'participated';
    default:
      return 'all';
  }
}
